import React, { useEffect, useState } from 'react';
import { Trash2, X, AlertCircle, LogOut, User } from 'lucide-react';
import { getStokKeluar, createStokKeluar, deleteStokKeluar, getInventoriSpareparts, getGudang } from '../../api';
import { useAuth } from '../../AuthContext';
import SearchableSelect from '../../components/SearchableSelect';

export default function StokKeluar() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), gudang_id: '', penerima: '',
    keperluan: '', keterangan: '', items: [{ sparepart_id: '', qty: 1, keterangan: '' }]
  });

  const load = () => {
    setLoading(true);
    Promise.all([getStokKeluar(), getInventoriSpareparts({}), getGudang()])
      .then(([res, spRes, gRes]) => { setData(res.data); setSpareparts(spRes.data || []); setGudangList(gRes.data || []); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { sparepart_id: '', qty: 1, keterangan: '' }] }));
  const updItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const delItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Stok yang ditampilkan mengikuti gudang terpilih (model stok per gudang)
  const effectiveStock = (p) => {
    const gid = Number(form.gudang_id) || 0;
    const pg = p.stok_per_gudang;
    if (pg && Object.keys(pg).length > 0) {
      if (!gid) return 0;
      return Number(pg[String(gid)]) || 0;
    }
    const home = Number(p.gudang_id) || 0;
    if (home > 0 && gid > 0 && home !== gid) return 0;
    return Number(p.stock) || 0;
  };
  const sparepartsForGudang = spareparts.map(p => ({ ...p, stock: effectiveStock(p) }));

  const handleSave = async () => {
    setError('');
    if (form.items.length === 0 || !form.items[0].sparepart_id) { setError('Minimal 1 item sparepart'); return; }
    if (!form.penerima) { setError('Nama penerima harus diisi'); return; }
    // Validasi stok per gudang
    for (const it of form.items) {
      if (!it.sparepart_id) continue;
      const p = spareparts.find(x => String(x.id) === String(it.sparepart_id));
      if (!p) continue;
      const avail = effectiveStock(p);
      if (avail <= 0) { setError(`"${p.name}" tidak tersedia di gudang terpilih (stok 0). Ganti gudang atau pilih item lain.`); return; }
      if (Number(it.qty) > avail) { setError(`Stok "${p.name}" di gudang ini tidak mencukupi: tersedia ${avail}, diminta ${it.qty}.`); return; }
    }
    setSaving(true);
    try {
      await createStokKeluar({
        tanggal: form.tanggal, gudang_id: parseInt(form.gudang_id) || 0,
        penerima: form.penerima, keperluan: form.keperluan, keterangan: form.keterangan,
        items: form.items.filter(i => i.sparepart_id).map(i => ({
          sparepart_id: Number(i.sparepart_id), qty: Number(i.qty), keterangan: i.keterangan
        }))
      });
      setShowModal(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus stok keluar ini?')) return;
    try { await deleteStokKeluar(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Stok Keluar</h2>
            <p className="page-desc">{data.length} transaksi</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={() => { setError(''); setShowModal(!showModal); }}>
              <LogOut size={16} /><span>{showModal ? 'Tutup Form' : 'Buat Stok Keluar'}</span>
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <div style={{
          marginTop: 16, padding: '20px 22px', borderRadius: 12,
          background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 14px rgba(15,23,42,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LogOut size={18} style={{ color: 'var(--primary, #4f46e5)' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Buat Stok Keluar</h3>
            </div>
            <button className="btn-icon" onClick={() => setShowModal(false)} title="Tutup form"><X size={18} /></button>
          </div>
          {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
          <div className="form-grid">
            <div className="form-group"><label>Tanggal *</label><input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} /></div>
            <div className="form-group"><label>Gudang</label>
              <select value={form.gudang_id} onChange={e => setForm({...form, gudang_id: e.target.value})}>
                <option value="">-- Pilih Gudang --</option>
                {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>Stok item mengikuti gudang terpilih — item yang tidak berada di gudang ini tampil <b>stok 0</b> dan tidak bisa dikeluarkan.</div>
            </div>
            <div className="form-group"><label>Nama Penerima *</label><input type="text" value={form.penerima} onChange={e => setForm({...form, penerima: e.target.value})} placeholder="Nama penerima" /></div>
            <div className="form-group"><label>Keperluan</label><input type="text" value={form.keperluan} onChange={e => setForm({...form, keperluan: e.target.value})} placeholder="Contoh: Pemakaian perbaikan AC" /></div>
          </div>
          <div className="form-group"><label>Keterangan</label><textarea rows={2} value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Catatan tambahan (opsional)" /></div>

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: 2 }}>Detail Item</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>Ketik nama / kode sparepart untuk mencari.</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '0 2px', marginBottom: 4 }}>
              <div style={{ flex: 3, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Sparepart *</div>
              <div style={{ width: 80, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Qty *</div>
              <div style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Keterangan</div>
              <div style={{ width: 28 }} />
            </div>
            {form.items.map((item, idx) => {
              const chosen = item.sparepart_id ? spareparts.find(x => String(x.id) === String(item.sparepart_id)) : null;
              const chosenAvail = chosen ? effectiveStock(chosen) : null;
              return (
              <div key={idx} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <SearchableSelect
                  options={sparepartsForGudang}
                  value={item.sparepart_id}
                  onChange={v => updItem(idx, 'sparepart_id', v)}
                  placeholder="Ketik kode / nama item..."
                />
                <input type="number" min="1" value={item.qty} onChange={e => updItem(idx, 'qty', e.target.value)} placeholder="0" style={{ width: 80, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <input type="text" value={item.keterangan} onChange={e => updItem(idx, 'keterangan', e.target.value)} placeholder="Keterangan item" style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                <button className="btn-icon btn-danger" onClick={() => delItem(idx)} disabled={form.items.length === 1} title="Hapus item"><X size={14} /></button>
                </div>
                {chosen && chosenAvail !== null && (
                  <div style={{ fontSize: '0.72rem', color: chosenAvail <= 0 ? '#f5365c' : '#2dce89', margin: '2px 0 0 2px' }}>
                    {chosenAvail <= 0
                      ? '⚠ Stok 0 di gudang terpilih — item ini tidak bisa dikeluarkan dari gudang ini.'
                      : `Stok tersedia di gudang ini: ${chosenAvail}`}
                  </div>
                )}
              </div>
              );
            })}
            <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px' }} onClick={addItem}>+ Tambah Item</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : <><LogOut size={16} /> Proses</>}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th><th>Tanggal</th><th>Gudang</th>
                <th>Penerima</th><th>Keperluan</th><th>Status</th><th>Dibuat Oleh</th>
                <th style={{ width: 80 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(d => (
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setDetailItem(d)}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td>{d.gudang_nama || '-'}</td>
                  <td><User size={12} /> {d.penerima || '-'}</td>
                  <td className="text-muted">{d.keperluan || '-'}</td>
                  <td><span className={`badge ${d.status === 'Selesai' ? 'badge-success' : ''}`}>{d.status}</span></td>
                  <td><span className="cell-with-icon"><User size={12} className="text-muted" />{d.created_by_name || '-'}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    {hasMenuAction('inventori', 'delete') && (
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Belum ada transaksi stok keluar</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detail Stok Keluar {detailItem.no_ref}</h3>
              <button className="btn-icon" onClick={() => setDetailItem(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div><label>Tanggal</label><div>{detailItem.tanggal}</div></div>
                <div><label>Gudang</label><div>{detailItem.gudang_nama || '-'}</div></div>
                <div><label>Penerima</label><div>{detailItem.penerima || '-'}</div></div>
                <div><label>Keperluan</label><div>{detailItem.keperluan || '-'}</div></div>
                <div><label>Dibuat Oleh</label><div>{detailItem.created_by_name || '-'}</div></div>
              </div>
              {detailItem.items && detailItem.items.length > 0 && (
                <table className="data-table">
                  <thead>
                    <tr><th>Sparepart</th><th>Qty</th><th>Keterangan</th></tr>
                  </thead>
                  <tbody>
                    {detailItem.items.map((it, i) => (
                      <tr key={i}>
                        <td>{it.code} - {it.name}</td>
                        <td style={{ fontWeight: 600 }}>{it.qty}</td>
                        <td>{it.keterangan || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
